import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import WidgetIdWrapper from '../shared/WidgetIdWrapper';
import useMediaQuery from '../custom-hooks/useMediaQuery';
import { device } from '../../utils/styled-component-mixins';
import TertiaryButtonWYSIWYG from '../shared/TertiaryButtonWYSIWYG';
import Accordion from '../shared/Accordion';
import { withWidgetSpacingWrapper } from '../shared/WidgetSpacingWrapper';

const TabsWrapperStyled = styled.div`
  [role='tablist'] {
    display: flex;
    justify-content: center;
    overflow: visible;
    width: 100%;
  }

  [role='tab'] {
    border: 0;
    position: relative;
    margin: 0;
    padding: 1.6rem 4rem;
    overflow: visible;
    font-size: 2.2rem;
    font-weight: 800;
    line-height: 3.2rem;
    white-space: normal;
  }

  [role='tab'][aria-selected='true'] {
    border-radius: 0;
    background-color: var(--slate-transparent);
  }

  [role='tab']:hover,
  [role='tab']:focus,
  [role='tab']:active {
    border-radius: 0;
    color: inherit;
  }

  .tabpanel-wrapper {
    background: linear-gradient(180deg, #e6e9ec 0%, var(--white) 100%);
    padding: 4rem 0 6.7rem 0;
  }

  [role='tabpanel'] {
    position: relative;
    z-index: 2;
  }

  [role='tabpanel'].is-hidden {
    display: none;
  }

  .tab-wysiwyg {
    line-height: 2.4rem;
  }
`;

const TabAccordionComponent = (props) => {
  const { properties, items } = props;
  const { layout, widgetID } = properties;
  const tabListRef = useRef(null);
  const isDesktop = useMediaQuery(device.desktop);

  const deactivateTabs = () => {
    const tabs = document.querySelectorAll('[role="tab"]');
    tabs.forEach((tab) => {
      tab.setAttribute('aria-selected', false);
    });

    const tabPanels = document.querySelectorAll('[role="tabpanel"]');
    tabPanels.forEach((tabPanel) => {
      tabPanel.classList.add('is-hidden');
    });
  };

  const activateTab = (tab) => {
    deactivateTabs();

    tab.setAttribute('aria-selected', true);
    const controls = tab.getAttribute('aria-controls');

    document.getElementById(controls).classList.remove('is-hidden');
  };

  const initActiveTab = (e) => {
    const url = window.location.hash;
    const uri = url?.split('#')[1];
    const tabs = document.querySelectorAll('[role="tab"]');
    const headerHeight = document.querySelector('header').offsetHeight;
    const localNavHeight = document.querySelector('.local-nav')?.offsetHeight;

    window.history.scrollRestoration = 'manual';
    tabs?.forEach((tab) => {
      const tabId = tab.getAttribute('id');
      if (tabId.toLowerCase() === uri) {
        activateTab(tab);

        if (e.type === 'load') {
          window.scroll(0, tab.offsetTop - headerHeight - localNavHeight);
        }
      }
    });
  };

  useEffect(() => {
    window.addEventListener('load', initActiveTab);
    window.addEventListener('hashchange', initActiveTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={tabListRef}>
      <WidgetIdWrapper widgetID={widgetID}>
        {isDesktop && layout === 'tab' && (
          <TabsWrapperStyled>
            <div role="tablist" aria-label="tabs">
              {items.map((item, index) => (
                <button
                  key={`${item.pageTitle}-${index}`}
                  type="button"
                  role="tab"
                  aria-controls={`${item.pageTitle}-tab`}
                  id={item.deepLinkID}
                  onClick={(event) => activateTab(event.currentTarget)}
                  aria-selected={`${index === 0 ? 'true' : 'false'}`}
                >
                  {item.pageTitle}
                </button>
              ))}
            </div>
            <div className="tabpanel-wrapper">
              {items.map((item, index) => (
                <div
                  aria-labelledby={item.pageTitle}
                  key={`${item.pageTitle}-${index}-tab`}
                  role="tabpanel"
                  id={`${item.pageTitle}-tab`}
                  className={`${index !== 0 && 'is-hidden'} container container-padding-md`}
                >
                  <div
                    className="tab-wysiwyg"
                    dangerouslySetInnerHTML={{ __html: item.content }}
                  ></div>
                  {item.secondaryContentArea && (
                    <TertiaryButtonWYSIWYG content={item.secondaryContentArea} />
                  )}
                </div>
              ))}
            </div>
          </TabsWrapperStyled>
        )}
        {(!isDesktop || layout === 'accordion') && (
          <div>
            {items.map((item, index) => (
              <Accordion
                key={`${item.pageTitle}`}
                {...item}
                index={index}
                className="accordion-item"
              />
            ))}
          </div>
        )}
      </WidgetIdWrapper>
    </div>
  );
};

export default withWidgetSpacingWrapper(TabAccordionComponent);
